import React, { useState, useEffect, useContext, useMemo, useCallback, useRef } from 'react';
import { TabView, TabPanel } from 'primereact/tabview';
import { Card } from 'primereact/card';
import TimeEntries from 'modules/billing/TimeEntries';
import Expenses from 'modules/billing/Expenses';
import { Tag } from 'primereact/tag';
import { InputSwitch } from 'primereact/inputswitch';
import { InputText } from 'primereact/inputtext';
import { PaymentRequests } from 'modules/billing/PaymentRequests';
import { PaymentHistory } from 'modules/billing/PaymentHistory';
import { Invoices } from 'modules/billing/Invoices';
import { getCaseBillingSummary, updateCase } from 'services/CaseServices';
import { useLocation, useHistory } from 'react-router-dom';
import { format } from 'date-fns';
import { dollarFormat, formatNumbersToCurrencyString, handleRedirection, removeUnderScoreFields } from 'utils/utils';
import { Button } from 'primereact/button';
import { updateLeadDetails } from 'services/leadServices';
import { useToast } from 'context/ToastContext';
import { toastConstant } from 'constants/toastmessage';
import { getSettleMentDetails, updateSettleMent } from 'services/expenseService';
import { handleDateTimeOffset } from 'utils/utility_functions/timezone';
import { UserDetailsContext } from 'context/userDetailsContext';
import constants from 'constants/index';
import NumberFormat from 'react-number-format';
import TrustAccounting from 'modules/billing/TrustAccounting';
import { paymentStructure } from 'constants/dropdownOptions';
import { clearLocalStorageItems, getLocalStorageItem, handleSettingSkAndPkToLocalStorage } from 'utils/localStorageUtils';
import TemporaryFunds from 'modules/billing/TemporaryFund/TemporaryFunds';
import { generateFullName } from 'utils/generateFullNameUtils';
import OperatingAccounting from 'modules/billing/OperatingAccounting';
import BillingPlaceholder from './BillingPlaceholder';
import NextPaymentPlaceholder from './NextPaymentPlaceholder';
import { Link } from 'react-router-dom/cjs/react-router-dom.min';
import TrustBalancePlaceholder from './TrustBalancePlaceholder';
import TrustHorizonPlaceholder from './TrustHorizonPlaceholder';
import GlobalTooltip from 'components/GlobalTooltip';
import useTrustHorizon from 'hooks/useTrustHorizon';
import { convertPaymentStructuresToLowerCase } from 'modules/billing/CreateInvoice/helpers/createInvoiceHelpers';

const Billing = (props) => {
  const { addToast } = useToast();
  const { caseDetails, getCaseDetails } = props;
  const [activeIndex, setActiveIndex] = useState();
  const [billingSummary, setBillingSummary] = useState({});
  const tabViewRef = useRef(null);
  const hasInitializedTabRef = useRef(false);

  // Calculate Temporary Funds tab index dynamically - it's always the last tab
  // Count all TabPanels that are rendered (all are always rendered, some just hidden)
  // All TabPanels count towards the index even if hidden
  const temporaryFundsTabIndex = useMemo(() => {
    // Count all TabPanels: Expenses(1) + AutoTime(1) + ManualTime(1) + PR(1) + Invoices(1) + PaymentHistory(1) + TrustAccounting(1 or hidden) + Operating(1) + TemporaryFunds(1) = 9 tabs
    // Since it's 0-indexed, Temporary Funds is at index 8
    // All TabPanels are always rendered (some hidden), so the count is always 9 = index 8
    return 8;
  }, []);

  const [preLitigation, setPreLitigation] = useState(0);
  const [litigation, setLitigation] = useState(0);
  const [attorneyFee, setAttorneyFee] = useState(0);
  const [isExpense, setIsExpense] = useState(false);
  const [settlementData, setSettlementData] = useState({});
  const [loading, setLoading] = useState(false);
  const userContext = useContext(UserDetailsContext);

  const history = useHistory();
  const location = useLocation();

  const clientDetails = useMemo(() => {
    return caseDetails?.client_details?.find((data) => data?.is_billing_contact === true);
  }, [caseDetails]);

  const { STANDARD_RETAINER } = convertPaymentStructuresToLowerCase() || {};

  const isStandardRetainer = caseDetails?.billing_preferences?.payment_structure?.toLowerCase() === STANDARD_RETAINER;

  const { trustHorizonData, trustHorizonLoading, trustHorizonError, refreshTrustHorizon } = useTrustHorizon(
    caseDetails?.case_id,
    caseDetails?.waiting_payments?.running_trust_balance,
    isStandardRetainer && props?.isFromCaseSpecificModule
  );

  const loadBillingSummary = async () => {
    let case_id = caseDetails?.case_id;
    setLoading(true);
    await getCaseBillingSummary({ case_id: case_id }, true).then((response) => {
      setLoading(false);
      if (response?.data && response?.data.length > 0) {
        setBillingSummary({ ...response?.data[0] });
        setPreLitigation(response?.data[0]?.billing_info?.pre_litigation);
        setLitigation(response?.data[0]?.billing_info?.litigation);
        setIsExpense(response?.data[0]?.billing_info?.is_expense);
      }
    });
  };

  const getSettleMent = async () => {
    let case_id = caseDetails?.case_id;
    await getSettleMentDetails(case_id).then((response) => {
      setAttorneyFee(response?.data?.attorney_fee);
      setSettlementData(response?.data);
    });
  };

  const updateCaseDetails = async () => {
    try {
      document.body.scrollTop = 0; // For Safari
      document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera
      let sendData = JSON.parse(JSON.stringify(props.caseDetails));

      sendData.billing_preferences.litigation = litigation.toString();
      sendData.billing_preferences.pre_litigation = preLitigation.toString();
      sendData.billing_preferences.is_expense = isExpense;
      sendData.stage_change = sendData?.case_description?.case_stage?.stage_name;

      if (!props.isLeadSpecific) {
        const updateCasePromise = updateCase(removeUnderScoreFields(sendData), null, null, props.isLeadSpecific);

        let promises = [updateCasePromise];

        if (typeof attorneyFee !== 'undefined') {
          const updateSettlementPromise = updateSettleMent({
            ...settlementData,
            attorney_fee: attorneyFee,
          });

          promises.push(updateSettlementPromise);
        }
        await Promise.all(promises)
          .then(async () => {
            const getCaseBillingSummaryPromise = loadBillingSummary();
            const getSettleMentDetails = getSettleMent();
            await Promise.all([getCaseBillingSummaryPromise, getSettleMentDetails]).then(() => {
              addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, toastConstant.message.CASE_UPDATED_CREATED);
            });
          })
          .catch(() => {
            addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, toastConstant.message.CASE_UPDATED_FAILED);
          });
      } else {
        const updateLeadDetailsPromise = updateLeadDetails(sendData.sk, sendData);

        let promises = [updateLeadDetailsPromise];

        if (typeof attorneyFee !== 'undefined') {
          const updateSettlementPromise = updateSettleMent({
            ...settlementData,
            attorney_fee: attorneyFee,
          });

          promises.push(updateSettlementPromise);
        }
        await Promise.all(promises)
          .then(async () => {
            const getCaseBillingSummaryPromise = loadBillingSummary();
            const getSettleMentDetails = getSettleMent();
            await Promise.all([getCaseBillingSummaryPromise, getSettleMentDetails]).then(() => {
              addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, toastConstant.message.CASE_UPDATED_CREATED);
            });
          })
          .catch(() => {
            addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, toastConstant.message.CASE_UPDATED_FAILED);
          });
      }
      // props.hideSpinner();
    } catch (error) {
      console.log('ERROR = ', error);
    }
  };

  const handleBillingRedirection = (e, rowData, route) => {
    e?.preventDefault();
    const { sk, pk } = rowData;
    if (sk && pk) handleSettingSkAndPkToLocalStorage(sk, pk);
    handleRedirection(history, route);
  };

  const getInvoiceTabIndex = () => {
    const isPersonalInjury = props?.caseDetails?.case_description?.case_practice_area === 'Personal Injury';
    const isContingencyFee = props?.caseDetails?.billing_preferences?.payment_structure === 'Contingency Fee';

    if (isPersonalInjury || isContingencyFee) {
      return -1;
    }

    let index = 0;

    index++;

    if (userContext.checkSubPermission('automatic_time_tracking')) {
      index++;
    }

    index++;

    if (!(isPersonalInjury || isContingencyFee)) {
      index++;
    }

    return index;
  };

  // const paymentPlanCreateInvoice = () => {
  //   setShowPaymentPlanCreateInvoice(true);
  //   const invoicesTabIndex = getInvoiceTabIndex();
  //   if (invoicesTabIndex >= 0) {
  //     setActiveIndex(invoicesTabIndex);
  //   }
  // };

  const getBalanceStatusTag = (status) => {
    switch (status) {
      case 'HEALTHY_BALANCE':
        return {
          value: 'HEALTHY BALANCE',
          className: 's-tag',
        };
      case 'LOW_BALANCE':
        return {
          value: 'LOW BALANCE',
          className: 'o-tag',
        };
      case 'ZERO_BALANCE':
        return {
          value: 'ZERO BALANCE',
          className: 'y-tag',
        };
      case 'NEGATIVE_BALANCE':
        return {
          value: 'NEGATIVE BALANCE',
          className: 'r-tag',
        };
      default:
        return {
          value: 'No Status',
          className: 'y-tag',
        };
    }
  };

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const invoiceSk = queryParams.get('invoice_sk');
    const paymentRequestSk = queryParams.get('payment_request_sk');
    const tabId = parseInt(queryParams.get('tab_id'), 10);

    if (
      !hasInitializedTabRef.current ||
      invoiceSk ||
      paymentRequestSk ||
      !isNaN(tabId) ||
      getLocalStorageItem('redirectToInvoice') ||
      getLocalStorageItem('viewInvoice') ||
      getLocalStorageItem('redirectToRequestPayment') ||
      getLocalStorageItem('viewPaymentRequest')
    ) {
      let activeIndexValue;

      if (invoiceSk) {
        activeIndexValue = 4;
        localStorage.removeItem('redirectToRequestPayment');
        localStorage.removeItem('viewPaymentRequest');
      } else if (paymentRequestSk) {
        activeIndexValue = 3;
        localStorage.removeItem('redirectToInvoice');
        localStorage.removeItem('viewInvoice');
      } else if (!isNaN(tabId)) {
        activeIndexValue = tabId;
      } else if (getLocalStorageItem('redirectToInvoice') || getLocalStorageItem('viewInvoice')) {
        activeIndexValue = 4;
      } else if (getLocalStorageItem('redirectToRequestPayment') || getLocalStorageItem('viewPaymentRequest')) {
        activeIndexValue = 3;
      } else if (!hasInitializedTabRef.current) {
        activeIndexValue = props?.caseDetails?.case_description?._case_practice_area_ === 'Personal Injury' ? 0 : 5;
      } else {
        return;
      }

      setActiveIndex(activeIndexValue);
      hasInitializedTabRef.current = true;
    }

    loadBillingSummary();
    getSettleMent();
    clearLocalStorageItems(['viewInvoice', 'viewPaymentRequest']);
  }, [location?.state, location?.search, localStorage]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const tabId = parseInt(queryParams.get('tab_id'), 10);
    // Check if `tab_id` exists and is a valid number
    if (!isNaN(tabId)) {
      setActiveIndex(tabId);
    }
  }, [location]);

  useEffect(() => {
    loadBillingSummary();
  }, [caseDetails]);

  return (
    <React.Fragment>
      <div className="row my-4">
        <div className="col-12 col-lg-5">
          <div className="caption-bold mb-2">Summary</div>
          {loading ? (
            <BillingPlaceholder />
          ) : (
            <Card className="card-bg">
              <div className="container px-0">
                <div className="row">
                  <div className="col-sm text-center col-6">
                    <div className="text-bold">{billingSummary?.unpaid_info?.unpaid_expenses}</div>
                    <div className="text-muted F-size12 pb-1 text-medium">Expenses</div>
                    <div className="text-muted F-size12">
                      <Tag className="da-tag" value="Unpaid" severity="danger"></Tag>
                    </div>
                  </div>
                  <div className="col-sm text-center col-6">
                    <div className="text-bold">{billingSummary?.unpaid_info?.unpaid_time_entries}h</div>
                    <div className="text-muted F-size12 pb-1 text-medium">Time entries</div>
                    <div className="text-muted F-size12">
                      <Tag className="da-tag" value="Unpaid" severity="danger"></Tag>
                    </div>
                  </div>
                  <div className="col-sm text-center col-6 mt-sm-20">
                    <div className="text-bold">{billingSummary?.unpaid_info?.unpaid_payment_request}</div>
                    <div className="text-muted F-size12 pb-1 text-medium">Requests</div>
                    <div className="text-muted F-size12">
                      <Tag className="da-tag" value="Unpaid" severity="danger"></Tag>
                    </div>
                  </div>
                  <div className="col-sm text-center col-6 mt-sm-20">
                    <div className="text-bold">{billingSummary?.unpaid_info?.unpaid_invoices}</div>
                    <div className="text-muted F-size12 pb-1 text-medium">Invoices</div>
                    <div className="text-muted F-size12">
                      <Tag className="da-tag" value="Unpaid" severity="danger"></Tag>
                      {/*   <Tag className="s-tag" value="Paid" severity="danger"></Tag> */}
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          )}
          <div className="caption-bold mt-5 mb-2">Trust Balance</div>
          {loading ? (
            <TrustBalancePlaceholder />
          ) : (
            <Card className="mb-3 card-bg waiting-payment mb-2">
              <div className="row mb-3">
                <div className="col-sm-12 text-muted small">Trust Funds Available for Billing</div>
                <div className="col-sm-12 F-size18 text-bold">
                  {formatNumbersToCurrencyString(Number(caseDetails?.waiting_payments?.running_trust_balance ?? 0))}
                </div>
              </div>
            </Card>
          )}

          {props?.isFromCaseSpecificModule && isStandardRetainer && (
            <>
              {trustHorizonLoading ? (
                <TrustHorizonPlaceholder />
              ) : trustHorizonError ? (
                <Card className="mb-2 card-bg">
                  <div className="row mb-3">
                    <div className="col-sm-12 d-flex align-items-center mb-2">
                      <div className="text-bold black-800 me-2">TRUST HORIZON</div>
                      <GlobalTooltip
                        tooltip="Projected trust balance after all unbilled work is invoiced. Calculated as: Current Trust Balance minus Uninvoiced Billable Time."
                        position="right"
                      >
                        <i className="pi pi-info-circle me-2 mt-1 pointer"></i>
                      </GlobalTooltip>
                    </div>
                    <div className="col-sm-12">
                      <div className="text-muted py-2">{trustHorizonError}</div>
                    </div>
                  </div>
                </Card>
              ) : !trustHorizonData || (Array.isArray(trustHorizonData) && trustHorizonData.length === 0) ? (
                <Card className="mb-2 card-bg">
                  <div className="row mb-3">
                    <div className="col-sm-12 d-flex align-items-center mb-2">
                      <div className="text-bold text-dark me-2">TRUST HORIZON</div>
                      <GlobalTooltip
                        tooltip="Projected trust balance after all unbilled work is invoiced. Calculated as: Current Trust Balance minus Uninvoiced Billable Time."
                        position="right"
                      >
                        <i className="pi pi-info-circle me-2 mt-1 pointer"></i>
                      </GlobalTooltip>
                    </div>
                    <div className="col-sm-12">
                      <div className="text-muted py-2">No data available</div>
                    </div>
                  </div>
                </Card>
              ) : (
                <Card className="mb-2 card-bg">
                  <div className="row mb-3">
                    <div className="col-sm-12 d-flex align-items-center mb-2">
                      <div className="text-bold black-800 me-2">LAWFT HORIZON</div>
                      <GlobalTooltip
                        tooltip="Projected trust balance after all unbilled work is invoiced. Calculated as: Current Trust Balance minus Uninvoiced Billable Time."
                        position="right"
                      >
                        <i className="pi pi-info-circle me-2 mt-1 pointer"></i>
                      </GlobalTooltip>
                    </div>
                    <div className="col-sm-12 mb-3">
                      <div className="F-size20 text-bold" style={{ color: '#ff6b35' }}>
                        {formatNumbersToCurrencyString(trustHorizonData?.trust_horizon || 0)}
                      </div>
                      <hr className="my-2" />
                    </div>
                    <div className="col-sm-12">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <div className="text-muted small">Current Trust Balance</div>
                        <div className="text-bold">{formatNumbersToCurrencyString(trustHorizonData?.current_trust_balance || 0)}</div>
                      </div>

                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <div className="text-muted small">Manual Time (Billable, not yet invoiced)</div>
                        <div className="text-bold text-danger">
                          {formatNumbersToCurrencyString(trustHorizonData?.manual_time_amount || 0)}
                        </div>
                      </div>

                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <div className="text-muted small">Automated Time (Billable, not yet invoiced)</div>
                        <div className="text-bold text-danger">
                          {formatNumbersToCurrencyString(trustHorizonData?.automated_time_amount || 0)}
                        </div>
                      </div>

                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <div className="text-muted small">Outstanding Invoice Amount</div>
                        <div className="text-bold text-danger">
                          {formatNumbersToCurrencyString(trustHorizonData?.outstanding_invoice_amount || 0)}
                        </div>
                      </div>

                      <hr className="my-2" />

                      <div className="d-flex justify-content-between align-items-center">
                        <div className="text-bold black-800">Trust Horizon</div>
                        <div className="text-bold black-800">{formatNumbersToCurrencyString(trustHorizonData?.trust_horizon || 0)}</div>
                      </div>

                      <div className="mt-2">
                        <Tag
                          value={getBalanceStatusTag(trustHorizonData?.balance_status).value}
                          className={getBalanceStatusTag(trustHorizonData?.balance_status).className}
                        />
                      </div>
                    </div>
                  </div>
                </Card>
              )}
            </>
          )}

          <div className="caption-bold mb-2 mt-4">Next PAYMENTS</div>
          {loading ? (
            <NextPaymentPlaceholder />
          ) : (
            <Card className="card-bg">
              {billingSummary?.next_payment?.length > 0 &&
                billingSummary?.next_payment?.map((index) => {
                  return (
                    <>
                      <div className="mb-2">
                        <div className="text-muted F-size12">Date</div>
                        <div className="text-bold F-size14">
                          {handleDateTimeOffset(userContext?.userDetails?.timezone, index.due_date, constants.month_date_year_format)}
                        </div>
                      </div>
                      <div className="mb-2">
                        <div className="text-muted F-size12">Amount</div>
                        <div className="text-bold F-size14">{dollarFormat(index.amount)}</div>
                      </div>
                    </>
                  );
                })}
              {!billingSummary?.next_payment ||
                (billingSummary?.next_payment && billingSummary?.next_payment.length === 0 && (
                  <>
                    <div className="mb-2">
                      <div className="text-muted F-size12">Date</div>
                      <div className="text-bold F-size14">-</div>
                    </div>
                    <div className="mb-2">
                      <div className="text-muted F-size12">Amount</div>
                      <div className="text-bold F-size14">-</div>
                    </div>
                  </>
                ))}
            </Card>
          )}
        </div>
        <div className="col-12 col-lg-7 mt-lg-0 mt-3">
          <div className="caption-bold mb-2">Billing info</div>
          <Card className="card-bg F-size14">
            <div className="d-flex align-items-center flex-wrap py-2">
              <div className="col-12 col-md-4 p-medium">Billing Account</div>
              <div className="col-md-8 col-12">
                <InputText className="input-shadow w-100" value={generateFullName(clientDetails)} disabled />
              </div>
            </div>
            <div className="d-flex align-items-center flex-wrap py-2">
              <div className="col-12 col-md-4 p-medium">Operating Account</div>
              <div className="col-md-8 col-12">
                <InputText
                  className="input-shadow w-100"
                  value={caseDetails?.default_operating_account?.[0]?.account_name || '-'}
                  disabled
                />
              </div>
            </div>
            <div className="d-flex align-items-center flex-wrap py-2">
              <div className="col-12 col-md-4 p-medium">Trust Account</div>
              <div className="col-md-8 col-12">
                <InputText className="input-shadow w-100" value={caseDetails?.default_trust_account?.[0]?.account_name || '-'} disabled />
              </div>
            </div>
            {/* {caseDetails?.billing_preferences?.custom_rates?.length > 0 && (
              <>
                <hr></hr>
                <div className="d-flex align-items-center flex-wrap py-2">
                  <div className="col-12 col-md-4 p-medium">Custom Rate</div>
                  <div className="col-md-8 col-12">
                    {caseDetails?.billing_preferences?.custom_rates?.map((index) => {
                      const route = index.team_member?.firm_user_id
                        ? `/settings/team-management/${index.team_member.firm_user_id}`
                        : '/settings/team-management';
                      return (
                        <div className="d-flex align-items-center flex-wrap py-2">
                          <div className="d-flex align-items-center">
                            <Link to={route} className={`me-2 text-decoration-none redirection-link-no-text-transform`}>
                              {index?.team_member?.firm_user_id && <i className="fas fa-user-tie me-2"></i>}
                              {generateFullName(index.team_member)}
                              <br />
                            </Link>
                            - &nbsp; <span className="text-bold">{dollarFormat(index.rate)}</span>{' '}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </>
            )}
            <hr></hr> */}
            <div className="d-flex align-items-center flex-wrap py-2">
              <div className="col-12 col-md-4 p-medium">Payment Structure</div>
              <div className="col-md-8 col-12">
                <InputText className="input-shadow w-100" value={billingSummary?.billing_info?.payment_structure} disabled />
              </div>
            </div>
            {!!billingSummary?.billing_info?.retainer_amount && (
              <div className="d-flex align-items-center flex-wrap py-2">
                <div className="col-12 col-md-4 p-medium">Retainer Amount</div>
                <div className="col-md-8 col-12">
                  <span className="p-input-icon-right w-100">
                    <InputText
                      className="input-shadow w-100"
                      value={dollarFormat(billingSummary?.billing_info?.retainer_amount)}
                      disabled
                    />
                  </span>
                </div>
              </div>
            )}
            {!!billingSummary?.billing_info?.rate &&
              [paymentStructure.FLAT_FEE].includes(billingSummary?.billing_info?.payment_structure) && (
                <div className="d-flex align-items-center flex-wrap py-2">
                  <div className="col-12 col-md-4 p-medium">Rate</div>
                  <div className="col-md-8 col-12">
                    <span className="p-input-icon-right w-100">
                      <InputText className="input-shadow w-100" value={dollarFormat(billingSummary?.billing_info?.rate)} disabled />
                    </span>
                  </div>
                </div>
              )}
            {!!billingSummary?.billing_info?.tax_percentage &&
              [paymentStructure.FLAT_FEE, paymentStructure.SUBSCIPTION].includes(billingSummary?.billing_info?.payment_structure) && (
                <div className="d-flex align-items-center flex-wrap py-2">
                  <div className="col-12 col-md-4 p-medium">Tax %</div>
                  <div className="col-md-8 col-12">
                    <span className="p-input-icon-right w-100">
                      <InputText className="input-shadow w-100" value={`${billingSummary?.billing_info?.tax_percentage}%`} disabled />
                    </span>
                  </div>
                </div>
              )}
            {!!billingSummary?.billing_info?.amount_with_tax &&
              [paymentStructure.FLAT_FEE, paymentStructure.SUBSCIPTION].includes(billingSummary?.billing_info?.payment_structure) && (
                <div className="d-flex align-items-center flex-wrap py-2">
                  <div className="col-12 col-md-4 p-medium">Total Amount</div>
                  <div className="col-md-8 col-12">
                    <span className="p-input-icon-right w-100">
                      <InputText
                        className="input-shadow w-100"
                        value={dollarFormat(billingSummary?.billing_info?.amount_with_tax)}
                        disabled
                      />
                    </span>
                  </div>
                </div>
              )}
            {billingSummary?.billing_info?.frequency_of_payment && (
              <div className="d-flex align-items-center flex-wrap py-2">
                <div className="col-12 col-md-4 p-medium">Frequency of Payment</div>
                <div className="col-md-8 col-12">
                  <span className="p-input-icon-right w-100">
                    <InputText className="input-shadow w-100" value={billingSummary?.billing_info?.frequency_of_payment} disabled />
                  </span>
                </div>
              </div>
            )}
            {billingSummary?.billing_info?.payment_plan_details?.first_payment_amount >= 0 && (
              <div className="d-flex align-items-center flex-wrap py-2">
                <div className="col-12 col-md-4 p-medium">First Payment Amount</div>
                <div className="col-md-8 col-12">
                  <span className="p-input-icon-right w-100">
                    <InputText
                      className="input-shadow w-100"
                      value={billingSummary?.billing_info?.payment_plan_details?.first_payment_amount}
                      disabled
                    />
                  </span>
                </div>
              </div>
            )}
            {billingSummary?.billing_info?.payment_plan_details?.payment_starting_date && (
              <div className="d-flex align-items-center flex-wrap py-2">
                <div className="col-12 col-md-4 p-medium">First Payment Date</div>
                <div className="col-md-8 col-12">
                  <span className="p-input-icon-right w-100">
                    <InputText
                      className="input-shadow w-100"
                      value={format(new Date(billingSummary?.billing_info?.payment_plan_details?.payment_starting_date), 'MM/dd/yyyy')}
                      disabled
                    />
                  </span>
                </div>
              </div>
            )}
            {billingSummary?.billing_info?.number_of_payments && (
              <div className="d-flex align-items-center flex-wrap py-2">
                <div className="col-12 col-md-4 p-medium">Number of Payments</div>
                <div className="col-md-8 col-12">
                  <span className="p-input-icon-right w-100">
                    <InputText className="input-shadow w-100" value={billingSummary?.billing_info?.number_of_payments} disabled />
                  </span>
                </div>
              </div>
            )}

            {billingSummary?.billing_info?.payment_structure === paymentStructure.CONTINGENCY_FEE && (
              <>
                {typeof attorneyFee !== 'undefined' && (
                  <div className="d-flex align-items-center flex-wrap py-2">
                    <div className="col-12 col-md-4 p-medium">Attorney Fee %</div>
                    <div className="col-md-8 col-12">
                      <span className="p-input-icon-right">
                        <NumberFormat
                          className="input-shadow w-100 p-2 number-format p-inputtext"
                          placeholder="Attorney Fee %"
                          value={attorneyFee}
                          suffix="%"
                          allowNegative={false}
                          decimalScale={2}
                          isAllowed={(values) => {
                            const { floatValue } = values;
                            return floatValue === undefined || floatValue <= 100;
                          }}
                          onValueChange={(e) => {
                            const { value } = e;
                            if (value === '.' && !value.startsWith('0')) {
                              setAttorneyFee('0' + value);
                            } else {
                              setAttorneyFee(value);
                            }
                          }}
                        />
                      </span>
                    </div>
                  </div>
                )}
                <div className="d-flex align-items-center flex-wrap py-2">
                  <div className="col-12 col-md-4 p-medium">If the case is resolved pre-litigation, the contingency fee is set at</div>
                  <div className="col-md-8 col-12">
                    <span className="p-input-icon-right">
                      <NumberFormat
                        className="input-shadow w-100 p-2 number-format p-inputtext"
                        placeholder="Pre Litigation"
                        allowNegative={false}
                        value={preLitigation}
                        suffix="%"
                        decimalScale={2}
                        isAllowed={(values) => {
                          const { floatValue } = values;
                          return floatValue === undefined || floatValue <= 100;
                        }}
                        onValueChange={(e) => {
                          const { value } = e;
                          if (value === '.' && !value.startsWith('0')) {
                            setPreLitigation('0' + value);
                          } else {
                            setPreLitigation(value);
                          }
                        }}
                      />
                    </span>
                  </div>
                </div>
                <div className="d-flex align-items-center flex-wrap py-2">
                  <div className="col-12 col-md-4 p-medium">If the case is resolved through litigation, the contingency fee is set at</div>
                  <div className="col-md-8 col-12">
                    <span className="p-input-icon-right">
                      <NumberFormat
                        className="input-shadow w-100 p-2 number-format p-inputtext"
                        placeholder="Litigation"
                        value={litigation}
                        allowNegative={false}
                        suffix="%"
                        decimalScale={2}
                        isAllowed={(values) => {
                          const { floatValue } = values;
                          return floatValue === undefined || floatValue <= 100;
                        }}
                        onValueChange={(e) => {
                          const { value } = e;
                          if (value === '.' && !value.startsWith('0')) {
                            setLitigation('0' + value);
                          } else {
                            setLitigation(value);
                          }
                        }}
                      />
                    </span>
                  </div>
                </div>
                <div className="d-flex align-items-center flex-wrap py-2">
                  <div className="col-12 col-md-4 p-medium">Expenses will be deducted from the settlement amount</div>
                  <div className="col-md-8 col-12">
                    <span className="p-input-icon-right w-100">
                      <InputSwitch className="input-swich" checked={isExpense} onChange={(e) => setIsExpense(e.value)} />
                    </span>
                  </div>
                </div>
                <div className="footer-btn mb-3">
                  <Button
                    label="Save"
                    className="btn-outline p-button-secondary py-1 px-3 "
                    type="button"
                    onClick={() => {
                      updateCaseDetails();
                    }}
                  />
                </div>
              </>
            )}
            {['Standard retainer', 'Evergreen retainer'].includes(billingSummary?.billing_info?.payment_structure) && (
              <>
                <div className="d-flex align-items-center flex-wrap py-2">
                  <div className="col-12 col-md-4 p-medium">Retainer Amount</div>
                  <div className="col-md-8 col-12">
                    <span className="p-input-icon-right w-100">
                      <i className="pi pi-dollar" />
                      <InputText className="w-100" value={billingSummary?.billing_info?.retainer_amount} disabled />
                    </span>
                  </div>
                </div>
                <div className="d-flex align-items-center flex-wrap py-2">
                  <div className="col-12 col-md-4 p-medium">Retainer Date</div>
                  <div className="col-md-8 col-12">
                    <span className="p-input-icon-right w-100">
                      <i className="pi pi-dollar" />
                      <InputText
                        className="w-100"
                        value={
                          billingSummary?.billing_info?.retainer_date
                            ? format(new Date(billingSummary?.billing_info?.retainer_date), 'MM/dd/yyyy')
                            : ''
                        }
                        disabled
                      />
                    </span>
                  </div>
                </div>
              </>
            )}

            {[paymentStructure.FLAT_FEE].includes(billingSummary?.billing_info?.payment_structure) && (
              <div className="d-flex align-items-center flex-wrap py-2">
                <div className="col-12 col-md-4 p-medium">Payment Plan</div>
                <div className="col-8 d-flex">
                  <InputSwitch className="input-swich pe-none" checked={billingSummary?.billing_info?.payment_plan} />
                </div>
              </div>
            )}
            {/* {billingSummary?.billing_info?.payment_plan && (
              <>
                <PaymentPlanTable
                  billingSummary={billingSummary}
                  setActiveIndex={setActiveIndex}
                  userContext={userContext}
                  caseView={true}
                  isLeadSpecific={props.isLeadSpecific}
                  caseDetails={props.caseDetails}
                  getCaseDetails={getCaseDetails}
                  isFromCaseSpecificModule={props.isFromCaseSpecificModule}
                  getTimeEntrySummarty={loadBillingSummary}
                  paymentPlanCreateInvoice={paymentPlanCreateInvoice}
                  setSelectedPaymentPlan={setSelectedPaymentPlan}
                />
              </>
            )} */}
          </Card>
        </div>
      </div>
      <div className="mt-5 billing-wrap billing-case">
        <div className="col-sm-12">
          <TabView
            ref={tabViewRef}
            className="curve-tab"
            activeIndex={activeIndex}
            onTabChange={(e) => {
              setActiveIndex(e.index);
            }}
            scrollable={true}
          >
            <TabPanel header="Expenses">
              <Expenses
                caseView={true}
                isLeadSpecific={props?.isLeadSpecific}
                isFromCaseSpecificModule={props.isFromCaseSpecificModule}
                caseDetails={props?.caseDetails}
                handleExpenseTableRedirection={handleBillingRedirection}
                getTimeEntrySummarty={loadBillingSummary}
              />
            </TabPanel>
            <TabPanel
              header="Automated Time Entries"
              headerStyle={{
                display: userContext.checkSubPermission('automatic_time_tracking') && props?.isFromCaseSpecificModule ? '' : 'none',
              }}
            >
              <TimeEntries
                caseView={true}
                isLeadSpecific={props?.isLeadSpecific}
                caseDetails={props?.caseDetails}
                is_automated={true}
                caseDetailsPage={true}
                isFromCaseSpecificModule={props?.isFromCaseSpecificModule}
                handleTimeEntryRedirection={handleBillingRedirection}
                onTrustHorizonRefresh={refreshTrustHorizon}
              />
            </TabPanel>
            <TabPanel header="Manual Time Entries">
              <TimeEntries
                caseView={true}
                isLeadSpecific={props.isLeadSpecific}
                caseDetails={props.caseDetails}
                is_automated={false}
                caseDetailsPage={true}
                isFromCaseSpecificModule={props.isFromCaseSpecificModule}
                handleTimeEntryRedirection={handleBillingRedirection}
                getTimeEntrySummarty={loadBillingSummary}
                isStandardRetainer={isStandardRetainer}
                onTrustHorizonRefresh={refreshTrustHorizon}
              />
            </TabPanel>
            <TabPanel
              header="Payment Requests"
              headerStyle={{
                display:
                  props?.caseDetails?.case_description?._case_practice_area_ === 'Personal Injury' ||
                  props?.caseDetails?.billing_preferences?.payment_structure === 'Contingency Fee'
                    ? 'none'
                    : '',
              }}
            >
              <PaymentRequests
                caseView={true}
                isLeadSpecific={props.isLeadSpecific}
                caseDetails={props.caseDetails}
                billingSummary={billingSummary}
                getCaseDetails={getCaseDetails}
                isFromCaseSpecificModule={props.isFromCaseSpecificModule}
                getTimeEntrySummarty={loadBillingSummary}
              />
            </TabPanel>
            <TabPanel
              header="Invoices"
              headerStyle={{
                display:
                  props?.caseDetails?.case_description?._case_practice_area_ === 'Personal Injury' ||
                  props?.caseDetails?.billing_preferences?.payment_structure === 'Contingency Fee'
                    ? 'none'
                    : '',
              }}
            >
              <Invoices
                caseView={true}
                isLeadSpecific={props.isLeadSpecific}
                caseDetails={props.caseDetails}
                getCaseDetails={getCaseDetails}
                isFromCaseSpecificModule={props.isFromCaseSpecificModule}
                getTimeEntrySummarty={loadBillingSummary}
                // showPaymentPlanCreateInvoice={showPaymentPlanCreateInvoice}
                // setShowPaymentPlanCreateInvoice={setShowPaymentPlanCreateInvoice}
                // selectedPaymentPlan={selectedPaymentPlan}
                // setSelectedPaymentPlan={setSelectedPaymentPlan}
                billingSummary={billingSummary}
                updateCaseDetails={props?.updateCaseDetails}
                onTrustHorizonRefresh={refreshTrustHorizon}
              />
            </TabPanel>
            <TabPanel
              header="Payment History"
              headerStyle={{
                display:
                  props?.caseDetails?.case_description?._case_practice_area_ === 'Personal Injury' ||
                  props?.caseDetails?.billing_preferences?.payment_structure === 'Contingency Fee'
                    ? 'none'
                    : '',
              }}
            >
              <PaymentHistory
                caseView={true}
                isLeadSpecific={props.isLeadSpecific}
                caseDetails={props.caseDetails}
                isFromCaseSpecificModule={props.isFromCaseSpecificModule}
                handlePaymentHistoryRedirection={handleBillingRedirection}
                getCaseDetails={getCaseDetails}
              />
            </TabPanel>
            {userContext.checkSubPermission('trust_accounting') ? (
              <TabPanel header="Trust Accounting">
                <TrustAccounting
                  caseView={true}
                  isLeadSpecific={props.isLeadSpecific}
                  caseDetails={props.caseDetails}
                  isFromCaseSpecificModule={props.isFromCaseSpecificModule}
                  getCaseDetails={getCaseDetails}
                  setActiveIndex={setActiveIndex}
                  temporaryFundsTabIndex={temporaryFundsTabIndex}
                />
              </TabPanel>
            ) : (
              <TabPanel header="" headerClassName="d-none" />
            )}
            <TabPanel
              header="Operating Accounting"
              headerStyle={{
                display:
                  props.isLeadSpecific && props?.caseDetails?.case_description?._case_practice_area_ === 'Personal Injury' ? 'none' : '',
              }}
            >
              <OperatingAccounting caseView={true} caseDetails={props.caseDetails} />
            </TabPanel>
            <TabPanel header="Temporary Funds">
              <TemporaryFunds
                caseView={true}
                isLeadSpecific={props.isLeadSpecific}
                caseDetails={props?.caseDetails}
                getCaseDetails={getCaseDetails}
                isFromCaseSpecificModule={props.isFromCaseSpecificModule}
              />
            </TabPanel>
          </TabView>
        </div>
      </div>
    </React.Fragment>
  );
};

export default Billing;
